import React from 'react';
import { connect } from 'react-redux';
import { Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CreatePageConstants, { DEFAULT_AUDIO_TRACK } from '../../CreatePageConstants';
import { createActions } from '../../store';
import { convertToLyricsTime } from '../../../../../utilities/lyricsUtilities';
import { RealtimeAudioAnalyzer } from '../../Audio/RealtimeAudioAnalyzer';
import { getAudioVolume } from '../../../../../utilities/storageUtilities';

const CursorIconSize = 34;
const draggingLineElementId = 'dragging-line-container';

const styles = theme => ({
    cursorDraggingLine: {
        position: 'absolute',
        width: 50,
        zIndex: 980,
        cursor: 'pointer',
        backgroundImage: 'url("https://specterr.b-cdn.net/ticks.png")',
        backgroundRepeat: 'repeat-y',
        backgroundPosition: '25px 0px'
    },
    cursorContainer: {
        position: 'absolute',
        width: CursorIconSize,
        height: CursorIconSize,
        zIndex: 990,
        cursor: 'pointer'
    },
    iconWrapper: {
        position: 'relative',
        bottom: 17
    }
});

const CursorIcon = withStyles(PlayArrowRoundedIcon, {
    root: {
        height: CursorIconSize,
        width: CursorIconSize
    },
});

class TimelineCursor extends React.Component {
    state = {
        shouldResume: false,
        shouldRenewPause: false,
        localPlaybackTime: 0
    }
    draggingLineElement = null;
    _rafId = null;
    _cursorRef = React.createRef();

    render() {
        const { classes, appliedPlaybackTime, audioDuration, previewPositionDragging, playingPreview } = this.props;
        const currentTime = (previewPositionDragging || !playingPreview) ? appliedPlaybackTime : this.state.localPlaybackTime;
        const position = currentTime * CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR - (CursorIconSize / 2);

        return (
            <Box
                id={draggingLineElementId} 
                className={classes.cursorDraggingLine}
                style={{
                    height: audioDuration * CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR, 
                }}
                onMouseDown={this.onDraggingLineMouseDown}
            >
                <Box
                    ref={this._cursorRef}
                    className={classes.cursorContainer}
                    style={{
                        top: 0,
                        transform: `translateY(${position}px)`,
                        willChange: 'transform'
                    }}
                >
                    <CursorIcon color='primary' />
                </Box>
            </Box>
        );
    }

    componentDidMount = () => {
        this.draggingLineElement = document.getElementById(draggingLineElementId);
        if (this.props.playingPreview) {
            this._startAnimationLoop();
        }
    }

    componentDidUpdate(prevProps) {
        const { playingPreview, previewPositionDragging, appliedPlaybackTime, setProperty } = this.props;

        if (!prevProps.playingPreview && playingPreview) {
            this._startAnimationLoop();
        } else if (prevProps.playingPreview && !playingPreview) {
            this._stopAnimationLoop();
            
            if (!previewPositionDragging) {
                setProperty('appliedPlaybackTime', this.state.localPlaybackTime);
            }
        }

        const timeChanged = prevProps.appliedPlaybackTime !== appliedPlaybackTime;
        const draggingStatusChanged = prevProps.previewPositionDragging !== previewPositionDragging;

        if (timeChanged || draggingStatusChanged) {
            this.setState({ localPlaybackTime: appliedPlaybackTime });
        }
    }

    componentWillUnmount = () => {
        this._stopAnimationLoop();
        this.draggingLineElement = null;
    }

    _startAnimationLoop = () => {
        this._stopAnimationLoop();
        const tick = () => {
            const currentTime = RealtimeAudioAnalyzer.getCurrentTime();
            if (!this.props.previewPositionDragging) {
                this.setState({ localPlaybackTime: currentTime });
            }
            this._rafId = requestAnimationFrame(tick);
        };
        this._rafId = requestAnimationFrame(tick);
    }

    _stopAnimationLoop = () => {
        if (this._rafId != null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    onDraggingLineMouseMove = (event) => {
        if (this.props.previewPositionDragging) {
            const { setProperty, audioDuration, appliedPlaybackTime } = this.props;
            let cursorTime = this.calculateCursorTime(this.draggingLineElement, event.pageY, audioDuration);

            if (Math.floor(cursorTime) !== Math.floor(appliedPlaybackTime)) 
                RealtimeAudioAnalyzer.setCurrentTime(cursorTime);

            if (RealtimeAudioAnalyzer.isPaused())                
                RealtimeAudioAnalyzer.play();

            this.setState({ localPlaybackTime: cursorTime });
            setProperty('appliedPlaybackTime', cursorTime);
        }
    }

    onDraggingLineMouseDown = (event) => {
        const { setProperty, audioDuration, playingPreview, setPlayingPreview, appliedPlaybackTime } = this.props;
        const cursorTime = this.calculateCursorTime(event.currentTarget, event.pageY, audioDuration);
        RealtimeAudioAnalyzer.setVolume(0);
        RealtimeAudioAnalyzer.setCurrentTime(cursorTime);
        
        if (RealtimeAudioAnalyzer.isPaused()) {
            RealtimeAudioAnalyzer.play();
        }

        this.setState({ localPlaybackTime: cursorTime }, () => {
            setProperty('appliedPlaybackTime', cursorTime);
            setProperty('previewPositionDragging', true);

            if (playingPreview) {
                setPlayingPreview(false);
                this.setState({shouldResume: true});
            } else {
                this.setState({ shouldRenewPause: true });
            }
        });

        this.assignDragAndDropEvents();
    }

    onStopDragging = (event) => {
        const { setProperty, audioDuration, audioMuted, setPlayingPreview } = this.props;
        const { shouldResume, shouldRenewPause } = this.state;

        setProperty('previewPositionDragging', false);
        this.removeDragAndDropEvents();

        if (shouldResume) {
            setPlayingPreview(true);
            this.setState({shouldResume: false});
        }

        if (shouldRenewPause) {
            RealtimeAudioAnalyzer.pause();
            this.setState({ shouldRenewPause: false });
        }

        setTimeout(() => {
            if (audioDuration !== DEFAULT_AUDIO_TRACK.DURATION && !audioMuted)
                RealtimeAudioAnalyzer.setVolume(getAudioVolume());
        }, 200)
    }

    assignDragAndDropEvents = () => {
        window.addEventListener('mousemove', this.onDraggingLineMouseMove);
        window.addEventListener('mouseup', this.onStopDragging);
        window.addEventListener('mouseleave', this.onStopDragging);

        document.onselectstart = () => { return false; };
        document.onselectionchange = () => { return false; };
    }

    removeDragAndDropEvents = () => {
        window.removeEventListener('mousemove', this.onDraggingLineMouseMove);
        window.removeEventListener('mouseup', this.onStopDragging);
        window.removeEventListener('mouseleave', this.onStopDragging);

        document.onselectstart = null;
        document.onselectionchange = null;
    }

    calculateCursorTime = (containerElement, pageY, audioDuration) => {
        const containerRect = containerElement.getBoundingClientRect();
        const cursorPosition = pageY - containerRect.top;
        let cursorTime = convertToLyricsTime(cursorPosition / CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR);

        if (cursorTime < 0)
            cursorTime = 0;

        if (cursorTime > audioDuration) 
            cursorTime = audioDuration;

        return cursorTime;
    }
}

const mapStateToProps = (state) => {
    const { 
        appliedPlaybackTime,
        audioDuration,
        audioMuted,
        previewPositionDragging,
        playingPreview
    } = state.create;

    return {
        appliedPlaybackTime,
        audioDuration,
        audioMuted,
        previewPositionDragging,
        playingPreview
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const setPlayingPreview = (playingPreview) => dispatch(createActions.setPlayingPreview(playingPreview));

    return {
        setProperty,
        setPlayingPreview
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(TimelineCursor, styles));